function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useState, useContext } from 'react';
import cn from 'clsx';
import { string, shape, bool, object, objectOf, arrayOf, number } from 'prop-types';
import warning from 'warning';
import { useTranslation } from 'react-i18next';
import FormGroup from '../FormGroup/index.jsx';
import FormFeedback from '../../atoms/FormFeedback/index.jsx';
import FormLabel from '../../atoms/FormLabel/index.jsx';
import FormInputGroup from '../FormInputGroup/index.jsx';
import Button from '../Button/index.jsx';
import IconStack from '../IconStack/index.jsx';
import FormContext from '../../context/FormContext.js';

/**
 * FormSortableInputs component.
 */
var _ref = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(IconStack.Icon, {
  name: "grab",
  color: "baseWhite",
  size: 16
}));
var _ref2 = /*#__PURE__*/React.createElement(IconStack, null, /*#__PURE__*/React.createElement(IconStack.Icon, {
  name: "cross",
  color: "baseWhite",
  size: 8
}), /*#__PURE__*/React.createElement(IconStack.Icon, {
  name: "circle",
  color: "backgroundRegular",
  size: 16
}));
const FormSortableInputs = ({
  name,
  className,
  stepSchema,
  droppableId,
  defaultSteps,
  reactBeautifulDnd: dnd
}) => {
  const {
    errors,
    defaultValues,
    reactBeautifulDnd = dnd
  } = useContext(FormContext);
  const {
    t
  } = useTranslation();
  if (!reactBeautifulDnd) {
    throw new Error('For optimisation purposes you must install "react-beautiful-dnd" ' + 'and pass it in via the `reactBeautifulDnd` prop of either this, or the root Form component.');
  }
  const {
    DragDropContext,
    Droppable,
    Draggable
  } = reactBeautifulDnd;

  // Get initial steps, filtering any that are invalid and setting IDs
  const initialSteps = (defaultSteps || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name]) || []).reduce((acc, step, index) => {
    if (!(step.type in stepSchema)) {
      process.env.NODE_ENV !== "production" ? warning(`Invalid step: type "${step.type}" does not exist in the schema`, {
        step,
        stepSchema
      }) : void 0;
      return acc;
    }
    return [...acc, {
      id: index + 1,
      type: step.type,
      value: step.value
    }];
  }, []);
  const [steps, setSteps] = useState(initialSteps);
  const [stepId, setStepId] = useState(initialSteps.length);
  const [mounted, setMounted] = useState(false);

  // Having one step is mandatory. Prevents the user removing all steps
  const showDeleteButton = steps.length > 1;
  const addStep = type => {
    setStepId(stepId + 1);
    const newSteps = [...steps, {
      id: stepId,
      type,
      value: ''
    }];
    setSteps(newSteps);
  };
  const removeStep = itemId => {
    const newStepData = [...steps];
    const foundStep = newStepData.findIndex(item => item.id === itemId);
    if (foundStep > -1) {
      newStepData.splice(foundStep, 1);
    }
    setSteps(newStepData);
  };

  /**
   * Reorders the steps once the drag finishes
   * @param {Object} result
   * A built in object from react-beautiful-dnd
   */
  const onDragEnd = result => {
    const {
      destination,
      source
    } = result;
    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }
    const newSteps = [...steps];
    const movedStep = newSteps[source.index];
    newSteps.splice(source.index, 1);
    newSteps.splice(destination.index, 0, movedStep);
    setSteps(newSteps);
  };
  useEffect(() => {
    /**
     * Add an input of the first type as the default. This is done
     * client side to avoid a mismatch with auto-generated IDs.
     * Also create a check to see if the component is mounted
     * otherwise react-beautiful-dnd context complains.
     */
    const firstKey = Object.keys(stepSchema)[0];
    if (initialSteps.length < 1 && firstKey) {
      addStep(firstKey);
    }
    setMounted(true);
  }, []);
  const renderInput = (inputName, snapshot, provided, schema, index, step) => {
    var _steps$index;
    return /*#__PURE__*/React.createElement("div", _extends({
      key: inputName,
      className: 'mb-sm' + (snapshot.isDragging ? " form-sortable-input--dragging" : "")
    }, provided.draggableProps, {
      ref: provided.innerRef
    }), /*#__PURE__*/React.createElement(FormGroup, {
      inputId: inputName,
      feedbackId: `${inputName}-feedback`
    }, /*#__PURE__*/React.createElement(FormLabel, null, `${schema.label}${schema.showLabelIndex ? ` ${index + 1}` : ''}`), /*#__PURE__*/React.createElement(FormInputGroup, {
      as: schema.textArea ? 'textarea' : null,
      rows: schema.textArea ? 3 : null,
      className: "form-sortable-input__input",
      placeholder: schema.placeholder,
      name: inputName,
      required: true,
      defaultValue: ((_steps$index = steps[index]) === null || _steps$index === void 0 ? void 0 : _steps$index.value) || '',
      prepend: /*#__PURE__*/React.createElement("div", _extends({
        "aria-label": t('molecules.formSortableInputs.ariaLabel.grab'),
        role: "button"
      }, provided.dragHandleProps, {
        className: "form-sortable-input__handle full-height" + (errors && inputName in errors ? " form-sortable-input__handle--invalid" : "")
      }), _ref),
      append: showDeleteButton && /*#__PURE__*/React.createElement(Button, {
        inputAppend: true,
        ariaLabel: `${t('molecules.formSortableInputs.ariaLabel.deleteInput')} ${schema.label} ${step.id + 1}`,
        type: "reset",
        transparent: true,
        onClick: () => {
          removeStep(step.id);
        }
      }, _ref2)
    }), /*#__PURE__*/React.createElement(FormFeedback, {
      name: inputName
    })));
  };

  // Render the input with a drag and drop wrapper
  return /*#__PURE__*/React.createElement("div", {
    className: cn('form-sortable-input', className)
  }, mounted && /*#__PURE__*/React.createElement(DragDropContext, {
    onDragEnd: onDragEnd
  }, /*#__PURE__*/React.createElement(Droppable, {
    droppableId: droppableId
  }, provided => /*#__PURE__*/React.createElement("div", _extends({
    ref: provided.innerRef
  }, provided.droppableProps), steps === null || steps === void 0 ? void 0 : steps.map((step, index) => {
    const schema = stepSchema[step.type];
    const inputName = `${step.type}:${step.id}`;
    return /*#__PURE__*/React.createElement(Draggable, {
      draggableId: inputName,
      index: index,
      key: inputName
    }, (_provided, snapshot) => renderInput(inputName, snapshot, _provided, schema, index, step));
  }), provided.placeholder))), /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-direction-row"
  }, Object.entries(stepSchema).map(([type, schema], index) => /*#__PURE__*/React.createElement(Button, {
    key: type,
    adaptive: true,
    className: index > 0 ? 'ml-xs' : "",
    secondary: schema.buttonTypeSecondary,
    small: true,
    onClick: () => {
      addStep(type);
    }
  }, schema.buttonText))));
};
FormSortableInputs.defaultProps = {
  className: null,
  defaultSteps: null,
  reactBeautifulDnd: null
};
FormSortableInputs.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The react-beautiful-dnd library. */
  reactBeautifulDnd: object,
  /** The name of the group. Note that this won't actually be a name of
    of an input field in the form data but helps us to map against, for example,
    `defaultValues` in the FormContext. */
  name: string.isRequired,
  /** Defines the drop zone for the draggable items */
  droppableId: string.isRequired,
  /** Defines the available inputs and buttons */
  stepSchema: objectOf(shape({
    label: string,
    showLabelIndex: bool,
    buttonText: string,
    placeholder: string
  })).isRequired,
  /** Additional class names to add to the component root. */
  className: string,
  /** Pass in some steps to prepopulate the inputs */
  defaultSteps: arrayOf(shape({
    id: number,
    type: string,
    value: string
  }))
} : {};
export default FormSortableInputs;